import { GadgetSystemDominionId } from "../../database/DominionLiterals";
import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyShopKey,
  ShopifySubscriptionContractKey,
  ShopifySubscriptionManualDiscountKey,
  ShopifySubscriptionManualDiscountToShopKey,
  ShopifySubscriptionManualDiscountToSubscriptionContractKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifySubscriptionManualDiscountKey,
  "2022-01": {
    name: "Shopify Subscription Manual Discount",
    fields: [
      {
        name: "Entitled Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "entitledLines", graphQLSelection: "all" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Recurring Cycle Limit",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "recurringCycleLimit" } },
      },
      {
        name: "Rejection Reason",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "rejectionReason", isShopifyEnum: true } },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [
            { name: "CURRENTLY_INACTIVE" },
            { name: "CUSTOMER_NOT_ELIGIBLE" },
            { name: "CUSTOMER_USAGE_LIMIT_REACHED" },
            { name: "INCOMPATIBLE_PURCHASE_TYPE" },
            { name: "INTERNAL_ERROR" },
            { name: "NOT_FOUND" },
            { name: "NO_ENTITLED_LINE_ITEMS" },
            { name: "NO_ENTITLED_SHIPPING_LINES" },
            { name: "PURCHASE_NOT_IN_RANGE" },
            { name: "QUANTITY_NOT_IN_RANGE" },
            { name: "USAGE_LIMIT_REACHED" },
          ],
        },
      },
      {
        name: "Target Type",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "targetType", isShopifyEnum: true } },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "LINE_ITEM" }, { name: "SHIPPING_LINE" }],
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "title" } },
      },
      {
        name: "Type",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "type", isShopifyEnum: true } },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "CODE_DISCOUNT" }, { name: "MANUAL" }, { name: "AUTOMATIC_DISCOUNT" }],
        },
      },
      {
        name: "Usage Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "usageCount" } },
      },
      {
        name: "Value",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "value",
            graphQLSelection: `...on SubscriptionDiscountFixedAmountValue {
          amount {
            amount
            currencyCode
          }
          appliesOnEachItem
        }
        ...on SubscriptionDiscountPercentageValue {
          percentage
        }`,
          },
        },
      },
      {
        name: "Subscription Contract",
        key: ShopifySubscriptionManualDiscountToSubscriptionContractKey,
        fieldType: FieldType.BelongsTo,
        configuration: {
          relatedModelKey: ShopifySubscriptionContractKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionManualDiscountToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      resource: "subscriptionContract.discounts",
      parent: "subscriptionContracts",
    },
  },
  "2024-07": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        key: `${ShopifySubscriptionManualDiscountKey}-system-id`,
        dominionID: GadgetSystemDominionId,
      },
      {
        name: "Admin GraphQL API ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Subscription Contract",
        key: ShopifySubscriptionManualDiscountToSubscriptionContractKey,
        fieldType: FieldType.BelongsTo,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "subscriptionContract", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifySubscriptionContractKey,
        },
      },
    ],
  },
};

export const ShopifySubscriptionManualDiscount = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
